import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaFacebookF, FaTwitter, FaLinkedinIn, FaBars, FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';

const getMenuItems = (t) => [
  { title: t('header.home'), path: '/' },
  { title: t('header.about'), path: '/a-propos' },
  { title: t('header.construction'), path: '/btp' },
  { title: t('header.realEstate'), path: '/promotion-immobiliere' },
  { title: t('header.mining'), path: '/mines' },
  { title: t('header.careers'), path: '/carrieres' },
  { title: t('header.contact'), path: '/contact' },
];

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const menuItems = getMenuItems(t);
  const allowedPaths = ['/', '/contact', '/a-propos', '/btp', '/promotion-immobiliere', '/mines', '/carrieres'];
  
  // Effet pour détecter le scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className={`bg-white sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}>
      {/* Top Bar (desktop only) */}
      <div className="w-full bg-white border-b border-gray-100 hidden xl:block">
        <div className="container mx-auto flex flex-row items-center py-2 text-socabeg-dark-gray text-sm justify-between">
          {/* Contact Info */}
          <div className="flex flex-row items-center gap-6">
            <span className="flex items-center gap-2">
              <FaMapMarkerAlt className="text-socabeg-blue" />
              <a
                href="https://www.google.com/maps/search/?api=1&query=14.6778611,-17.4637778"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-socabeg-blue transition-colors duration-200"
              >
                Fann-Hock – Corniche face Cour Suprême, BP 1270, Dakar, Sénégal
              </a>
            </span>
            <span className="flex items-center gap-2">
              <FaPhoneAlt className="text-socabeg-blue" />
              <a href="tel:+221338893535" className="hover:text-socabeg-blue transition-colors duration-200">+221 33 889 35 35</a>
            </span>
            <span className="flex items-center gap-2">
              <FaEnvelope className="text-socabeg-blue" />
              <a href="mailto:infos@socabeg.com" className="hover:text-socabeg-blue transition-colors duration-200">infos@socabeg.com</a>
            </span>
          </div>
          
          {/* Social icons and language selector */}
          <div className="flex items-center gap-4">
            <a href="https://www.facebook.com/socabeg" target="_blank" rel="noopener noreferrer" className="text-socabeg-blue hover:text-socabeg-blue/80 transition-colors duration-200"><FaFacebookF /></a>
            <a href="https://www.twitter.com/socabeg" target="_blank" rel="noopener noreferrer" className="text-socabeg-blue hover:text-socabeg-blue/80 transition-colors duration-200"><FaTwitter /></a>
            <a href="https://www.linkedin.com/company/socabeg" target="_blank" rel="noopener noreferrer" className="text-socabeg-blue hover:text-socabeg-blue/80 transition-colors duration-200"><FaLinkedinIn /></a>
            <div className="ml-4 border-l border-gray-200 pl-4">
              <LanguageSelector />
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="container mx-auto px-4 py-4 flex items-center justify-between flex-wrap">
        {/* Logo */}
        <div className="flex items-center">
          <Link to="/" className="flex-shrink-0">
            <img
              src="/logo.png"
              alt="SOCABEG Logo"
              className={`transition-all duration-300 object-contain ${isScrolled ? 'h-10' : 'h-12'}`}
            />
          </Link>
        </div>

        {/* Espace flexible pour pousser le menu à droite */}
        <div className="flex-1 xl:hidden"></div>
        
        {/* Menu desktop */}
        <nav className="hidden xl:flex items-center space-x-4 lg:space-x-6 xl:space-x-8 justify-end flex-wrap">
          {menuItems.map((item, index) => {
            const isAllowed = allowedPaths.includes(item.path);
            const isActive = location.pathname === item.path;
            if (!isAllowed) {
              return (
                <span
                  key={index}
                  className="relative text-base font-medium py-2 text-socabeg-dark-gray"
                >
                  {item.title}
                </span>
              );
            }
            return (
              <Link
                key={index}
                to={item.path}
                className={`relative text-base font-medium transition-colors duration-200 py-2 ${
                  isActive
                    ? "text-socabeg-blue after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-socabeg-blue after:rounded-full"
                    : "text-socabeg-dark-gray hover:text-socabeg-blue"
                }`}
              >
                {item.title}
              </Link>
            );
          })}
        </nav>



        {/* Mobile Menu Button */}
        <div className="xl:hidden ml-2 md:ml-4">
          <button 
            onClick={toggleMobileMenu} 
            className="w-10 h-10 flex items-center justify-center rounded-full bg-transparent text-socabeg-blue transition-all duration-200 hover:text-socabeg-blue/80 focus:outline-none"
            aria-label="Menu"
          >
            {isMobileMenuOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="xl:hidden bg-white/80 backdrop-blur-sm px-4 py-6 absolute w-full left-0 top-full"
          >
            <div className="container mx-auto bg-transparent">
              {menuItems.map((item, index) => {
                const isAllowed = allowedPaths.includes(item.path);
                const isActive = location.pathname === item.path;
                if (!isAllowed) {
                  return (
                    <span
                      key={index}
                      className="block py-3 text-base border-b border-gray-100/30 last:border-0 text-socabeg-dark-gray"
                    >
                      {item.title}
                    </span>
                  );
                }
                return (
                  <Link
                    key={index}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block py-3 text-base border-b border-gray-100/30 last:border-0 ${
                      isActive
                        ? "text-socabeg-blue font-medium"
                        : "text-socabeg-dark-gray hover:text-socabeg-blue"
                    }`}
                  >
                    {item.title}
                  </Link>
                );
              })}
              
              {/* Language selector mobile */}
              <div className="flex justify-start mt-4 pt-4 border-t border-gray-100/30">
                <LanguageSelector mobileVersion={true} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}